import type { i18n as I18nInstance } from 'i18next';
import type { BasketTableAnswerItem, EducationContent, QuestionConfigQuestionType } from '/index';

import useFormatSharedSupplierScope1 from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedSupplierScope1';
import { initiativeVisuals } from '/business/shared/components/questions/helper/initiativeVisuals';
import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import formatNumber from '/shared/functions/formatNumber';

import useBuildingOtherFuelsEmissionsSchema from '/business/shared/components/questions/scope1/_hooks/useBuildingOtherFuelsEmissionsSchema';
import FlameIcon from '/business/shared/images/icons/icon-flame.svg';
import initiativeCardBackground from '/business/shared/images/initiatives/Fuel-card-1x.jpg';
import initiativeBackground from '/business/shared/images/initiatives/Fuel-card-2x.jpg';

import useFormatSharedCustomScope1 from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedCustomScope1';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import useAppContext from '/business/shared/hooks/useAppContext';

interface ConfigureOtherFuelsOptions {
  hasScopeBasedOrganisation?: boolean;
  i18n: I18nInstance;
}

type FuelQuantityRow = {
  fuelUnit?: string;
  harmonisedInjection: (fuelUnit: string) => string;
  quantity: number;
};

type FuelTonnageRow = {
  tonnage: number;
};

function getOtherFuelsEducationContent(value: EducationContent | undefined): EducationContent {
  if (!value) {
    throw new Error('Other fuels education content is not configured');
  }
  return value;
}

export const getQuestionBuildingOtherFuels = ({
  i18n,
  hasScopeBasedOrganisation = true,
}: ConfigureOtherFuelsOptions): QuestionConfigQuestionType => {
  return {
    id: 'other',
    scopeCategory: hasScopeBasedOrganisation
      ? questionScopeCategories.SCOPE_1
      : questionScopeCategories.BUILDING_EMISSIONS,
    useHookLoader: useBuildingOtherFuelsEmissionsSchema,
    useQuestionBranchSupplierLoader: () =>
      useFormatSharedSupplierScope1({
        questionFormatName: 'other',
        emissionSourcePlaceholder: 'Select type of fuel',
        sourceErrMsg: 'Please select a type of fuel',
      }),
    useQuestionBranchCustomLoader: () => useFormatSharedCustomScope1({ questionFormatName: 'other' }),
    isCompletePropName: 'other',
    apiKey: 'calculation-building-emissions-other',
    title: i18n.t('business:Questions.calculations.question3.title'),
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.other',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.otherTip',
    overviewPageTitleI18nKey: 'business:Questions.calculations.question3.title',
    icon: FlameIcon,
    reductionPageKey: 'otherFuels',
    reductionPageChartTitle: i18n.t('business:QuestionPage.forms.otherFuels.key'),
    reductionPageCards: ['slide1', 'slide2', 'slide3', 'slide4', 'slide5'],
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          What fuels have been consumed by your business during the reporting period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter data about fuels that have been consumed by your business during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    hideSelectorTip: true,
    table: {
      title: i18n.t('business:Questions.calculations.question3.table.title'),
      calculateTotal: (allItems: BasketTableAnswerItem[]) => allItems.length,
      totalType: basketTableTotalTypes.COUNT,
      headings: [
        i18n.t('business:Questions.calculations.question3.table.heading1'),
        i18n.t('business:Questions.calculations.question3.table.heading2'),
        i18n.t('business:Questions.calculations.question3.table.heading3'),
      ],
      cells: [
        'fuelType',
        ({ quantity, fuelUnit = 'units', harmonisedInjection }: FuelQuantityRow) =>
          `${formatNumber(quantity)} ${i18n.t('business:emissionFactors.volumes.'.concat(fuelUnit), fuelUnit)} ${harmonisedInjection(fuelUnit)}`,
        ({ tonnage }: FuelTonnageRow) => `${formatNumber(tonnage)} tCO\u2082e`,
      ],
      isAnswerEditable: false,
      supplier: {
        columns: ['emissionSource', 'supplierName', 'scope1orAnd3Intensity', 'quantity', 'carbonEmissions'],
      },
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },
    details: {
      title: 'fuelType',
      items: [
        'fuelType',
        ({ quantity, fuelUnit = 'units' }: Omit<FuelQuantityRow, 'harmonisedInjection'>) =>
          `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(fuelUnit), fuelUnit)}`,
        ({ tonnage }: FuelTonnageRow) => `${formatNumber(tonnage)} ${i18n.t('business:Snippets.tonnes')}`,
        {
          value: 'dataSource',
          title: i18n.t('business:Questions.calculations.question3.details.title'),
        },
      ],
    },
    initiative: {
      getVisuals: ({ frontendEnum, questionTypeKey = null, isBpOffering }) => {
        if (!isBpOffering) {
          return {
            description: <>test</>,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
        if (isBpOffering) {
          const { cardImage, headerImage } = initiativeVisuals(frontendEnum, questionTypeKey);
          return {
            description: <>test1</>,
            background: headerImage,
            useMask: false,
            cardBackground: cardImage,
          };
        }
      },
      formatForInitiativeSummary: ({ initiativeAnswerEnum = null } = {}) => {
        return {
          isShowingReportingPeriod: false,
          isShowingReportingPeriodNoReduction: true,
          category: initiativeAnswerEnum,
          description: `Remaining ${initiativeAnswerEnum} consumption for fuels burned on-site to reduce after incorporating the initiatives already added to the plan so far`,
          descriptionNoReduction: `${initiativeAnswerEnum} consumption for fuels burned on-site during the reporting period `,
        };
      },
    },
    selector: {
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with the fuels used on-site by your business, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: i18n.t('business:Questions.selectors.other.activity.requiredData'),
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData: i18n.t('business:Questions.selectors.other.supplier.requiredData'),
        },
        {
          title: questionSelectorOptions.CUSTOM,
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: () => {
        const { questionsPage } = useAppContext();
        return getOtherFuelsEducationContent(questionsPage?.education?.otherFuelsEducation);
      },
    },
  };
};
